import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { flushSync } from 'react-dom';
import {
    Button,
    Col,
    Divider,
    Drawer,
    Form,
    FormProps,
    Grid,
    Input,
    message,
    Row,
    Segmented,
    Select,
    Table,
} from 'antd';
import {
    CloseOutlined,
    CopyOutlined,
    EyeOutlined,
    FileTextOutlined,
    SearchOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { ColumnsType } from 'antd/lib/table';
import { topTitleContext } from '../../../contexts/TopTitle';

import { useGetTotemsAdmin } from '../../../store/Totem/useGetTotemsAdmin';
import {
    TotemAdminItem,
    TotemsListParams,
} from '../../../store/Totem/typesAdmin';
import { TotemProps } from '../../Store/DetailsStore/Tabs/types';
import Indicators from '../../Store/DetailsStore/Tabs/Indicators';
import TotemsReport from '../TotemsReport';
import { useAuth } from '../../../contexts/Auth';
import { EnRoles } from '../../../interfaces/Roles';

enum TotemsView {
    List = 'list',
    Report = 'report',
}

const viewLabel = (icon: React.ReactNode, label: string, isActive: boolean) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: isActive ? '#1677ff' : undefined,
        }}
    >
        {icon}
        {label}
    </span>
);

const totemAdminToTotemProps = (t: TotemAdminItem): TotemProps => ({
    id: t.id,
    store_id: t.store_id,
    android_uid: t.android_uid,
    nome: t.nome,
    serial_id: t.serial_id,
    created_at: t.created_at,
    deleted_at: null,
    updated_at: t.updated_at,
    last_log_id: null,
    hardware_model: t.hardware_model,
});

const TYPE_OPTIONS = [
    { label: 'Totem SMART', value: 'Smart' },
    { label: 'Totem com MINIZINHA', value: 'Tablet' },
    { label: 'Totem PIX', value: 'Pix' },
    { label: 'Totem Aux (Tablet + Smart)', value: 'Aux' },
];

const AUTH_OPTIONS = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false },
];

interface FilterFormValues {
    totemId?: string;
    storeId?: string;
    shopmanagerId?: string;
    androidUid?: string;
    hardwareModel?: string;
    nome?: string;
    isAuthenticated?: boolean | null;
    type?: string;
    version?: string;
}

const parseOptionalInt = (v?: string) => {
    if (v === undefined || v === null || String(v).trim() === '')
        return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
};

const isSmartBySerialId = (serialId?: string | null) => {
    const s = (serialId ?? '').trim().toUpperCase();
    return s.startsWith('600') || s.startsWith('PBA') || s.startsWith('PB3');
};

const getDisplayType = (record: TotemAdminItem) => {
    if (isSmartBySerialId(record.android_uid)) return 'Smart';
    return record.type ?? '—';
};

const formatNomeUpper = (nome?: string | null) => {
    const s = (nome ?? '').trim();
    return s ? s.toLocaleUpperCase('pt-BR') : '—';
};

const formatSerialIdDisplay = (serialId?: string | null) => {
    const s = (serialId ?? '').trim();
    if (s === 'XXX') return '---';
    return s || '—';
};

/** Serial copiável na listagem: não vazio e não placeholder XXX. */
const canCopySerialId = (serialId?: string | null) => {
    const s = (serialId ?? '').trim();
    if (!s) return false;
    if (s.toUpperCase() === 'XXX') return false;
    return true;
};

const getLastLog = (record: TotemAdminItem) => record.lastLog ?? record.lastlog;

const formatTextOrDash = (value?: string | null) => {
    const text = (value ?? '').trim();
    return text || '---';
};

const formatNumberOrDash = (value?: number | null, suffix = '') => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '---';
    return `${value}${suffix}`;
};

const formatLastPing = (iso?: string | null) => {
    const s = (iso ?? '').trim();
    if (!s) return '---';
    const m = moment(s);
    return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '---';
};

const TotemsManagement: React.FC = () => {
    const { titleValue } = useContext(topTitleContext);

    const { data: authData } = useAuth();
    const isSysAdmin = authData?.role?.some(
        (role: { type: string }) => role.type === EnRoles.sysAdmin,
    );
    const [view, setView] = useState<TotemsView>(TotemsView.List);

    const breakpoints = Grid.useBreakpoint();
    const isMobile = !breakpoints.md;
    const [form] = Form.useForm<FilterFormValues>();
    const [params, setParams] = useState<TotemsListParams>({
        page: 1,
        perPage: 10,
    });

    const { data, isFetching } = useGetTotemsAdmin(params);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailRecord, setDetailRecord] = useState<TotemAdminItem | null>(
        null,
    );

    const openTotemDetail = useCallback((record: TotemAdminItem) => {
        setDetailRecord(record);
        setDetailOpen(true);
    }, []);

    const copySerialId = useCallback((serialId?: string | null) => {
        const raw = (serialId ?? '').trim();
        if (!canCopySerialId(serialId)) {
            message.warning('Sem serial para copiar');
            return;
        }
        navigator.clipboard.writeText(raw).then(
            () => message.success('Serial copiado'),
            () => message.error('Não foi possível copiar'),
        );
    }, []);

    const closeTotemDetail = useCallback(() => {
        setDetailOpen(false);
        setDetailRecord(null);
    }, []);

    const handlePaginationChange = useCallback(
        (page: number, pageSize?: number) => {
            flushSync(() => {
                setParams((prev) => ({
                    ...prev,
                    page,
                    perPage: pageSize !== undefined ? pageSize : prev.perPage,
                }));
            });
        },
        [],
    );

    useEffect(() => {
        titleValue('Totens');
    }, [titleValue]);

    const columns: ColumnsType<TotemAdminItem> = useMemo(
        () => [
            {
                title: 'ID',
                dataIndex: 'id',
                width: 80,
                fixed: isMobile ? undefined : ('left' as const),
            },
            {
                title: 'Nome',
                dataIndex: 'nome',
                ellipsis: true,
                render: (_: unknown, record) => formatNomeUpper(record.nome),
            },
            {
                title: 'Android UID',
                dataIndex: 'android_uid',
                ellipsis: true,
                render: (_: unknown, record) => {
                    const display = formatSerialIdDisplay(record.android_uid);
                    const showCopy = canCopySerialId(record.android_uid);
                    return (
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                maxWidth: '100%',
                            }}
                        >
                            <span
                                style={{
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {display}
                            </span>
                            {showCopy && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={
                                        <CopyOutlined
                                            style={{
                                                fontSize: 12,
                                                color: '#91caff',
                                            }}
                                        />
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        copySerialId(record.android_uid);
                                    }}
                                    aria-label="Copiar serial"
                                    style={{
                                        flexShrink: 0,
                                        padding: '0 4px',
                                        color: '#91caff',
                                    }}
                                />
                            )}
                        </span>
                    );
                },
            },
            {
                title: 'Modelo',
                dataIndex: 'hardware_model',
                ellipsis: true,
            },
            {
                title: 'Dispositivo',
                key: 'type_display',
                width: 110,
                render: (_: unknown, record) => getDisplayType(record),
            },
            {
                title: 'Lojista',
                dataIndex: 'shopmanager_id',
                width: 90,
            },
            {
                title: 'Loja',
                dataIndex: 'store_id',
                width: 90,
            },
            {
                title: 'Status',
                key: 'status',
                width: 170,
                render: (_: unknown, record) => {
                    const auth = getLastLog(record)?.authenticated;
                    const ok = auth === 1;
                    return (
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '5px 12px',
                                borderRadius: 999,
                                fontSize: 9,
                                fontWeight: 600,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                background: ok ? '#f0fdf4' : '#fff5f5',
                                color: ok ? '#166534' : '#9f1239',
                                border: `1px solid ${
                                    ok ? '#bbf7d0' : '#fecdd3'
                                }`,
                                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                            }}
                        >
                            {ok ? 'Autenticado' : 'Não autenticado'}
                        </span>
                    );
                },
            },
            {
                title: 'Versão',
                key: 'version',
                width: 170,
                ellipsis: true,
                render: (_: unknown, record) =>
                    formatTextOrDash(getLastLog(record)?.version),
            },
            {
                title: 'Bateria',
                key: 'battery_level',
                width: 110,
                render: (_: unknown, record) =>
                    formatNumberOrDash(getLastLog(record)?.battery_level, '%'),
            },
            {
                title: 'Wi-Fi',
                key: 'wifi_name',
                width: 180,
                ellipsis: true,
                render: (_: unknown, record) =>
                    formatTextOrDash(getLastLog(record)?.wifi_name),
            },
            {
                title: 'Sinal Wi-Fi',
                key: 'wifi_signal_percent',
                width: 120,
                render: (_: unknown, record) =>
                    formatNumberOrDash(
                        getLastLog(record)?.wifi_signal_percent,
                        '%',
                    ),
            },
            {
                title: 'Último Ping',
                key: 'last_log_created_at',
                width: 160,
                render: (_: unknown, record) =>
                    formatLastPing(getLastLog(record)?.created_at),
            },
            {
                title: '',
                key: 'details',
                width: 56,
                fixed: isMobile ? undefined : ('right' as const),
                align: 'center',
                render: (_: unknown, record) => (
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => openTotemDetail(record)}
                        aria-label="Ver detalhes"
                    />
                ),
            },
        ],
        [copySerialId, openTotemDetail, isMobile],
    );

    const onSearch: FormProps<FilterFormValues>['onFinish'] = (values) => {
        setParams({
            page: 1,
            perPage: params.perPage,
            totemId: parseOptionalInt(values.totemId),
            storeId: parseOptionalInt(values.storeId),
            shopmanagerId: parseOptionalInt(values.shopmanagerId),
            androidUid: values.androidUid,
            hardwareModel: values.hardwareModel,
            nome: values.nome,
            version: values.version,
            isAuthenticated:
                values.isAuthenticated === null ||
                values.isAuthenticated === undefined
                    ? undefined
                    : values.isAuthenticated,
            type: values.type,
        });
    };

    const onReset = () => {
        form.resetFields();
        setParams({ page: 1, perPage: 10 });
    };

    return (
        <>
            {isSysAdmin && (
                <Row style={{ padding: '0px 20px', marginBottom: 16 }}>
                    <Col span={24}>
                        <div style={{ fontSize: 12, marginBottom: 8 }}>
                            VISÃO
                        </div>
                        <Segmented<TotemsView>
                            size="large"
                            value={view}
                            onChange={setView}
                            options={[
                                {
                                    label: viewLabel(
                                        <UnorderedListOutlined />,
                                        'Listagem de totens',
                                        view === TotemsView.List,
                                    ),
                                    value: TotemsView.List,
                                },
                                {
                                    label: viewLabel(
                                        <FileTextOutlined />,
                                        'Relatório de totens',
                                        view === TotemsView.Report,
                                    ),
                                    value: TotemsView.Report,
                                },
                            ]}
                        />
                    </Col>
                </Row>
            )}
            {isSysAdmin && view === TotemsView.Report && <TotemsReport />}
            <Row
                style={{
                    padding: '0px 20px',
                    display: view === TotemsView.Report ? 'none' : undefined,
                }}
            >
                <Col span={24}>
                    <Form form={form} layout="vertical" onFinish={onSearch}>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="ID do totem"
                                    name="totemId"
                                >
                                    <Input inputMode="numeric" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="ID da loja"
                                    name="storeId"
                                >
                                    <Input inputMode="numeric" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="ID do lojista"
                                    name="shopmanagerId"
                                >
                                    <Input inputMode="numeric" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Android UID"
                                    name="androidUid"
                                >
                                    <Input allowClear />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Nome do totem"
                                    name="nome"
                                    rules={[
                                        {
                                            min: 3,
                                            message: 'Mínimo 3 caracteres',
                                        },
                                    ]}
                                >
                                    <Input allowClear />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Modelo hardware"
                                    name="hardwareModel"
                                >
                                    <Input allowClear />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Tipo"
                                    name="type"
                                >
                                    <Select
                                        allowClear
                                        placeholder="Todos"
                                        options={TYPE_OPTIONS}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Autenticado"
                                    name="isAuthenticated"
                                >
                                    <Select
                                        allowClear
                                        placeholder="Todos"
                                        options={AUTH_OPTIONS}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Versão do software"
                                    name="version"
                                >
                                    <Input allowClear />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row style={{ paddingTop: '1rem' }} gutter={[8, 16]}>
                            <Col xs={24} sm={8} md={6} xl={5} xxl={4}>
                                <Button
                                    htmlType="submit"
                                    className="default-btn"
                                    icon={<SearchOutlined />}
                                    loading={isFetching}
                                    style={{
                                        width: breakpoints.sm
                                            ? undefined
                                            : '100%',
                                    }}
                                >
                                    Pesquisar
                                </Button>
                            </Col>
                            <Col xs={24} sm={8} md={6} xl={5} xxl={4}>
                                <Button
                                    danger
                                    onClick={onReset}
                                    style={{ width: '100%' }}
                                >
                                    Limpar
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                    <Divider />
                    <Row justify="space-between" style={{ margin: 0 }}>
                        <Col>
                            <span style={{ fontSize: 14 }}>
                                Resultados: {data?.meta.total ?? 0}
                            </span>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: 24 }}>
                        <Col span={24}>
                            <Table<TotemAdminItem>
                                key={`totems-${params.page}-${params.perPage}`}
                                rowKey="id"
                                style={{ width: '100%' }}
                                dataSource={data?.data ?? []}
                                columns={columns}
                                loading={isFetching}
                                scroll={{ x: 'max-content' }}
                                pagination={{
                                    total: data?.meta.total ?? 0,
                                    current: params.page,
                                    pageSize: params.perPage,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                    onChange: handlePaginationChange,
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Drawer
                closable={false}
                title={
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 12,
                        }}
                    >
                        <span style={{ minWidth: 0, textAlign: 'right' }}>
                            {detailRecord
                                ? `Status do totem · ${formatNomeUpper(
                                      detailRecord.nome,
                                  )}`
                                : 'Status do totem'}
                        </span>
                        <button
                            type="button"
                            aria-label="Fechar"
                            className="ant-drawer-close"
                            onClick={closeTotemDetail}
                            style={{ marginRight: 0 }}
                        >
                            <CloseOutlined />
                        </button>
                    </div>
                }
                placement="left"
                width="100vw"
                open={detailOpen}
                onClose={closeTotemDetail}
                destroyOnClose
                styles={{ body: { padding: 0, background: '#f0f2f5' } }}
            >
                {detailRecord && (
                    <Indicators
                        key={`${detailRecord.store_id}-${detailRecord.id}`}
                        data={{ id: detailRecord.store_id }}
                        initialTotemId={detailRecord.id}
                        embedded
                        embeddedTotem={totemAdminToTotemProps(detailRecord)}
                        forceAdminActions
                    />
                )}
            </Drawer>
        </>
    );
};

export default TotemsManagement;
